import { useEffect } from "react";
import { getIndustryReads } from "../../data/industryReads";
import { getNewsArticle, getNewsArticles } from "../../data/newsArticles";
import { useLocale } from "../../i18n/LocaleContext";
import { buildLocalizedPath } from "../../lib/siteRouting";

type Props = {
  slug: string;
};

export function NewsDetailPage({ slug }: Props) {
  const { locale } = useLocale();
  const article = getNewsArticle(locale, slug);
  const relatedArticles = getNewsArticles(locale).filter((item) => item.slug !== slug).slice(0, 2);
  const industryReads = getIndustryReads(locale).slice(0, 3);
  const detailPath = buildLocalizedPath(locale, `/news/${slug}`);
  const topGallery = article?.galleryImages?.slice(0, 3) ?? [];
  const galleryLabel =
    locale === "zh"
      ? "活动图集"
      : locale === "ko"
        ? "이벤트 갤러리"
        : locale === "vi"
          ? "Thư viện hình ảnh"
          : "Event Gallery";
  const relatedLabel =
    locale === "zh"
      ? "相关新闻"
      : locale === "ko"
        ? "관련 소식"
        : locale === "vi"
          ? "Tin liên quan"
          : "Related News";
  const industryLabel =
    locale === "zh"
      ? "行业相关新闻"
      : locale === "ko"
        ? "산업 읽을거리"
        : locale === "vi"
          ? "Tin ngành liên quan"
          : "Industry Reads";
  const externalLabel =
    locale === "zh"
      ? "外部来源"
      : locale === "ko"
        ? "외부 링크"
        : locale === "vi"
          ? "Nguồn ngoài"
          : "External Source";
  const eventTypeLabel =
    locale === "zh"
      ? "活动类型"
      : locale === "ko"
        ? "활동 유형"
        : locale === "vi"
          ? "Loại hoạt động"
          : "Activation Type";
  const keyBriefLabel =
    locale === "zh"
      ? "重点摘要"
      : locale === "ko"
        ? "핵심 요약"
        : locale === "vi"
          ? "Tóm tắt chính"
          : "Key Brief";

  useEffect(() => {
    if (!article) return;
    const pageTitle = `${article.detailTitle} | QIANDU`;
    const canonical = `https://www.qiandu.group${detailPath}`;

    const setMeta = (selector: string, value: string, attr = "content") => {
      const node = document.head.querySelector(selector);
      if (node) {
        node.setAttribute(attr, value);
      }
    };

    document.title = pageTitle;
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[name="twitter:title"]', pageTitle);
    setMeta('meta[name="description"]', article.summary);
    setMeta('meta[property="og:description"]', article.summary);
    setMeta('meta[name="twitter:description"]', article.summary);
    setMeta('meta[property="og:url"]', canonical);

    const canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonical);
    }
  }, [article, detailPath]);

  if (!article) {
    return (
      <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
        <div className="mx-auto max-w-5xl rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.86),rgba(240,246,249,0.94))] p-10 text-center shadow-[0_12px_24px_rgba(106,138,151,0.05)]">
          <a
            href={buildLocalizedPath(locale, "/")}
            className="inline-flex rounded-full border border-[#dce8ee] bg-white/80 px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64"
          >
            Back to Home
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
      <div className="mx-auto max-w-6xl">
        <a
          href={buildLocalizedPath(locale, "/")}
          className="inline-flex rounded-full border border-[#dce8ee] bg-[rgba(251,254,255,0.82)] px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64 shadow-[0_10px_22px_rgba(106,138,151,0.06)] backdrop-blur"
        >
          {locale === "zh" ? "返回首页" : locale === "ko" ? "홈으로 돌아가기" : locale === "vi" ? "Quay lại trang chủ" : "Back to Home"}
        </a>

        <div className="mt-8 overflow-hidden rounded-[2.8rem] border border-[#dce8ee] bg-[radial-gradient(circle_at_top_left,rgba(196,218,227,0.22),transparent_28%),linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))] p-6 shadow-[0_14px_26px_rgba(106,138,151,0.06)] sm:p-8">
          <div className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr] lg:items-start">
            <div>
              <div className="text-[8px] uppercase tracking-[0.56em] text-aurora/72">{article.detailEyebrow}</div>
              <h1 className="mt-5 max-w-[42rem] font-display text-[2.08rem] leading-[1.02] text-[#1f2b31] sm:text-[3rem]">
                {article.detailTitle}
              </h1>
              <div className="mt-5 text-[11px] uppercase tracking-[0.28em] text-soft/44">{article.date}</div>
              <p className="mt-6 max-w-[36rem] text-[0.92rem] leading-8 text-soft/60">{article.intro}</p>

              <div className="mt-7 flex flex-wrap gap-2.5">
                {article.highlights.map((item) => (
                  <span
                    key={item}
                    className="rounded-full bg-[rgba(252,255,255,0.82)] px-3.5 py-2 text-[8px] uppercase tracking-[0.32em] text-soft/48 shadow-[0_6px_14px_rgba(106,138,151,0.03)]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid gap-4">
              <div className="overflow-hidden rounded-[2.2rem] border border-white/55 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(242,248,251,0.94))] p-4 shadow-[0_10px_20px_rgba(106,138,151,0.05)]">
                <img
                  src={topGallery[0]?.src ?? article.image}
                  alt={topGallery[0]?.alt ?? article.title}
                  className={`h-[22rem] w-full rounded-[1.6rem] ${article.imageFit === "contain" ? "object-contain bg-[rgba(246,249,250,0.94)] p-4" : "object-cover object-center"}`}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                <div className="rounded-[2rem] border border-white/55 bg-white/72 p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] backdrop-blur">
                  <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{eventTypeLabel}</div>
                  <div className="mt-3 font-display text-[1.18rem] leading-tight text-[#1f2b31]">
                    {article.detailEyebrow}
                  </div>
                </div>
                <div className="rounded-[2rem] border border-white/55 bg-white/72 p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] backdrop-blur">
                  <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{keyBriefLabel}</div>
                  <p className="mt-3 text-[0.86rem] leading-6 text-soft/58">{article.summary}</p>
                </div>
              </div>

              {topGallery.length > 1 ? (
                <div className="rounded-[2.2rem] border border-white/55 bg-[linear-gradient(180deg,rgba(252,254,255,0.86),rgba(240,246,249,0.92))] p-4 shadow-[0_10px_20px_rgba(106,138,151,0.05)]">
                  <div className="text-[8px] uppercase tracking-[0.38em] text-soft/42">{galleryLabel}</div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {topGallery.slice(1).map((image) => (
                      <div
                        key={image.src}
                        className="overflow-hidden rounded-[1.45rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]"
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className={`h-40 w-full ${image.fit === "contain" ? "object-contain bg-[rgba(246,249,250,0.94)] p-3" : "object-cover object-center"}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {article.sections.map((section) => (
            <article
              key={section.title}
              className="rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-6 shadow-[0_10px_20px_rgba(106,138,151,0.05)]"
            >
              <div className="text-[8px] uppercase tracking-[0.34em] text-soft/42">{section.title}</div>
              <p className="mt-4 text-[0.9rem] leading-7 text-soft/60">{section.body}</p>
            </article>
          ))}
        </div>

        {article.galleryImages?.length ? (
          <div className="mt-12 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(240,246,249,0.92))] p-5 shadow-[0_12px_24px_rgba(106,138,151,0.05)]">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {article.galleryImages.map((image) => (
                <div
                  key={image.src}
                  className="overflow-hidden rounded-[1.8rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]"
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={`h-64 w-full ${image.fit === "contain" ? "object-contain bg-[rgba(246,249,250,0.94)] p-3" : "object-cover object-center"}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {relatedArticles.length ? (
          <div className="mt-12 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(240,246,249,0.92))] p-5 shadow-[0_12px_24px_rgba(106,138,151,0.05)] sm:p-6">
            <div className="text-[8px] uppercase tracking-[0.4em] text-aurora/72">{relatedLabel}</div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {relatedArticles.map((item) => (
                <a
                  key={item.slug}
                  href={buildLocalizedPath(locale, `/news/${item.slug}`)}
                  className="rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))] p-4 shadow-[0_10px_20px_rgba(106,138,151,0.05)] transition hover:-translate-y-1 hover:shadow-[0_14px_24px_rgba(106,138,151,0.07)]"
                >
                  <div className="overflow-hidden rounded-[1.4rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className={`h-40 w-full ${item.imageFit === "contain" ? "object-contain bg-[rgba(246,249,250,0.94)] p-3" : "object-cover object-center"}`}
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-4">
                    <div className="inline-flex rounded-full bg-[rgba(239,248,251,0.82)] px-3 py-1 text-[8px] uppercase tracking-[0.3em] text-[#6f8d99]">
                      {item.tag}
                    </div>
                    <div className="text-[8px] uppercase tracking-[0.3em] text-soft/44">{item.date}</div>
                  </div>
                  <h3 className="mt-4 font-display text-[1.18rem] leading-tight text-[#1f2b31]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[0.84rem] leading-6 text-soft/58">{item.summary}</p>
                </a>
              ))}
            </div>
          </div>
        ) : null}

        {industryReads.length ? (
          <div className="mt-12 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(240,246,249,0.92))] p-5 shadow-[0_12px_24px_rgba(106,138,151,0.05)] sm:p-6">
            <div className="text-[8px] uppercase tracking-[0.4em] text-aurora/72">{industryLabel}</div>
            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              {industryReads.map((item) => (
                <a
                  key={`${item.source}-${item.url}`}
                  href={buildLocalizedPath(locale, `/industry/${item.slug}`)}
                  className="rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))] p-5 shadow-[0_10px_20px_rgba(106,138,151,0.05)] transition hover:-translate-y-1 hover:shadow-[0_14px_24px_rgba(106,138,151,0.07)]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="inline-flex rounded-full bg-[rgba(239,248,251,0.82)] px-3 py-1 text-[8px] uppercase tracking-[0.3em] text-[#6f8d99]">
                      {item.source}
                    </div>
                    <div className="text-[8px] uppercase tracking-[0.3em] text-soft/44">{item.date}</div>
                  </div>
                  <h3 className="mt-4 font-display text-[1.08rem] leading-tight text-[#1f2b31]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[0.84rem] leading-6 text-soft/58">{item.summary}</p>
                  <div className="mt-5 text-[8px] uppercase tracking-[0.34em] text-aurora/72">{externalLabel}</div>
                </a>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-12 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.82),rgba(240,246,249,0.9))] px-6 py-8 shadow-[0_10px_20px_rgba(106,138,151,0.05)] sm:px-8">
          <div className="text-[8px] uppercase tracking-[0.4em] text-aurora/72">
            {locale === "zh" ? "新闻联系" : locale === "ko" ? "뉴스 문의" : locale === "vi" ? "Liên hệ" : "News Contact"}
          </div>
          <h2 className="mt-4 font-display text-[1.8rem] leading-tight text-[#1f2b31] sm:text-[2.2rem]">
            {locale === "zh"
              ? "如需了解品牌合作与市场活动详情，请联系 QIANDU。"
              : locale === "ko"
                ? "브랜드 협업 및 시장 활동 관련 문의는 QIANDU로 연락해 주세요."
                : locale === "vi"
                  ? "Liên hệ QIANDU để trao đổi thêm về hợp tác thương hiệu và hoạt động thị trường."
                  : "Contact QIANDU for further discussion on brand partnerships and market activations."}
          </h2>
          <a
            href="mailto:service@qiandu.group?subject=QIANDU%20News%20Inquiry"
            className="mt-6 inline-flex rounded-full bg-[linear-gradient(135deg,#88aebb,#9fc8d2)] px-6 py-3.5 text-[9px] uppercase tracking-[0.36em] text-white shadow-[0_10px_20px_rgba(123,167,184,0.16)]"
          >
            service@qiandu.group
          </a>
        </div>
      </div>
    </main>
  );
}
